<script>
import { defineAsyncComponent, getCurrentInstance, useTemplateRef } from 'vue';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useTrack } from 'dashboard/composables';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

import ReplyToMessage from './ReplyToMessage.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import CopilotReplyBottomPanel from 'dashboard/components/widgets/WootWriter/CopilotReplyBottomPanel.vue';
import ArticleSearchPopover from 'dashboard/routes/dashboard/helpcenter/components/ArticleSearch/SearchPopover.vue';
import CopilotEditorSection from './CopilotEditorSection.vue';
import MessageSignatureMissingAlert from './MessageSignatureMissingAlert.vue';
import ReplyBoxBanner from './ReplyBoxBanner.vue';
import QuotedEmailPreview from './QuotedEmailPreview.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import CameraCaptureModal from 'dashboard/components/widgets/WootWriter/CameraCaptureModal.vue';
import LocationMessageModal from 'dashboard/components/widgets/WootWriter/LocationMessageModal.vue';
import StickerCatalogModal from 'dashboard/components/widgets/WootWriter/StickerCatalogModal.vue';
import VideoCaptureModal from 'dashboard/components/widgets/WootWriter/VideoCaptureModal.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CMD_AI_ASSIST } from 'dashboard/helper/commandbar/events';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
} from '@chatwoot/utils';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import WhatsappFlows from './WhatsappFlows/Modal.vue';
import WhatsappCommerce from './WhatsappCommerce/Modal.vue';
import ContentTemplates from './ContentTemplates/ContentTemplatesModal.vue';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import { trimContent, debounce, getRecipients } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import {
  extractQuotedEmailText,
  buildQuotedEmailHeader,
  truncatePreviewText,
  appendQuotedTextToMessage,
} from 'dashboard/helper/quotedEmailHelper';
import {
  CONVERSATION_EVENTS,
  CAPTAIN_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  appendSignature,
  removeSignature,
  getEffectiveChannelType,
  getAgentVariables,
  getContactVariables,
} from 'dashboard/helper/editorHelper';
import { useCopilotReply } from 'dashboard/composables/useCopilotReply';
import { useMacroExecution } from 'dashboard/composables/useMacroExecution';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { isFileTypeAllowedForChannel } from 'shared/helpers/FileHelper';
import ConversationApi from 'dashboard/api/conversations';
import MessageApi from 'dashboard/api/inbox/message';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

export default {
  components: {
    ArticleSearchPopover,
    AttachmentPreview,
    AudioRecorder,
    CameraCaptureModal,
    LocationMessageModal,
    StickerCatalogModal,
    VideoCaptureModal,
    ReplyBoxBanner,
    EmojiIconPicker,
    MessageSignatureMissingAlert,
    ReplyBottomPanel,
    ReplyEmailHead,
    ReplyToMessage,
    ReplyTopPanel,
    ContentTemplates,
    WhatsappCommerce,
    WhatsappFlows,
    WhatsappTemplates,
    WootMessageEditor,
    QuotedEmailPreview,
    CopilotEditorSection,
    CopilotReplyBottomPanel,
    ConversationResolveAttributesModal,
  },
  mixins: [inboxMixin, fileUploadMixin],
  emits: ['toggleEditorSize'],
  setup() {
    const {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
    } = useUISettings();

    const messageEditor = useTemplateRef('messageEditor');
    const copilot = useCopilotReply();
    const macroExecution = useMacroExecution();
    const shortcutKey = useKbd(['$mod', '+', 'enter']);

    // Options API state and methods live on the instance proxy
    const { proxy } = getCurrentInstance();
    useKeyboardEvents({
      Escape: {
        action: () => proxy.hideEmojiPicker(),
        allowOnFocusedInput: true,
      },
      '$mod+KeyK': {
        action: e => {
          e.preventDefault();
          const ninja = document.querySelector('ninja-keys');
          ninja.open();
        },
        allowOnFocusedInput: true,
      },
      Enter: {
        action: e => {
          if (proxy.isAValidEvent('enter')) {
            proxy.onSendReply();
            e.preventDefault();
          }
        },
        allowOnFocusedInput: true,
      },
      '$mod+Enter': {
        action: () => {
          if (copilot.isActive.value && proxy.isFocused) {
            proxy.onSubmitCopilotReply();
          } else if (proxy.isAValidEvent('cmd_enter')) {
            proxy.onSendReply();
          }
        },
        allowOnFocusedInput: true,
      },
    });

    return {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
      messageEditor,
      copilot,
      shortcutKey,
      macroExecution,
    };
  },
  data() {
    return {
      message: '',
      inReplyTo: {},
      isFocused: false,
      showEmojiPicker: false,
      attachedFiles: [],
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      availableAudioInputDevices: [],
      availableVideoInputDevices: [],
      hasCheckedAudioInputDevices: false,
      hasCheckedVideoInputDevices: false,
      selectedAudioInputDeviceId: '',
      selectedVideoInputDeviceId: '',
      preferredVideoCaptureMimeType: '',
      replyType: REPLY_EDITOR_MODES.REPLY,
      draftConversationId: null,
      draftReplyMode: null,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      requestContactInfoTemplatesOnly: false,
      showWhatsAppFlowsModal: false,
      showWhatsAppCommerceModal: false,
      showContentTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      showMacrosMenu: false,
      newConversationModalActive: false,
      showArticleSearchPopover: false,
      hasRecordedAudio: false,
      copilotAcceptedMessages: {},
      createInternalChatConversation: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      currentRole: 'getCurrentRole',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
      accountId: 'getCurrentAccountId',
      isMetaMessageSendingDisabled: 'globalConfig/isMetaMessageSendingDisabled',
      getAccount: 'accounts/getAccount',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    isMacrosEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.MACROS
      );
    },
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },
    internalChatSettings() {
      return this.currentAccount?.settings?.internal_chat_settings || {};
    },
    internalChatInboxId() {
      const inboxId = this.internalChatSettings?.inbox_id;
      return inboxId ? Number(inboxId) : null;
    },
    internalChatInbox() {
      if (!this.internalChatInboxId) {
        return null;
      }

      const inbox = this.$store.getters['inboxes/getInbox'](
        this.internalChatInboxId
      );
      return inbox?.channel_type === 'Channel::Api' ? inbox : null;
    },
    internalChatContactId() {
      return Number(
        this.currentChat?.meta?.sender?.id || this.currentContact?.id || 0
      );
    },
    canCreateInternalChatConversation() {
      return (
        this.isOnPrivateNote &&
        this.internalChatSettings?.enabled === true &&
        !!this.internalChatInbox &&
        this.internalChatContactId > 0
      );
    },
    currentContact() {
      const senderId = this.currentChat?.meta?.sender?.id;
      if (!senderId) return {};
      return this.$store.getters['contacts/getContact'](senderId);
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.isPrivate &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        !this.is360DialogWhatsAppChannel &&
        !this.copilot.isActive.value
      );
    },
    showWhatsappTemplates() {
      // We support templates for API channels if someone updates templates manually via API
      // That's why we don't explicitly check for channel type here
      const templates = this.$store.getters['inboxes/getWhatsAppTemplates'](
        this.inboxId
      );
      return !!(templates && templates.length) && !this.isPrivate;
    },
    showContentTemplates() {
      return this.isATwilioWhatsAppChannel && !this.isPrivate;
    },
    showWhatsappFlows() {
      return this.isAWhatsAppCloudChannel && !this.isPrivate;
    },
    showWhatsappCommerce() {
      return this.isAWhatsAppCloudChannel && !this.isPrivate;
    },
    isWithinMessagingWindow() {
      return !!(
        this.currentChat?.can_reply ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox
      );
    },
    canSendPublicReply() {
      return (
        this.isWithinMessagingWindow &&
        !this.isBotOwnedPendingConversation &&
        !this.isInstagramReplyRestricted
      );
    },
    isInstagramReplyRestricted() {
      return this.isMetaMessageSendingDisabled && this.isAnInstagramChannel;
    },
    isPrivate() {
      return (
        !this.canSendPublicReply || this.replyType === REPLY_EDITOR_MODES.NOTE
      );
    },
    isOnPrivateNote() {
      if (this.isInstagramReplyRestricted) {
        return true;
      }

      return this.isBotOwnedPendingConversation
        ? this.isPrivate
        : this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    effectiveReplyMode() {
      return this.isOnPrivateNote
        ? REPLY_EDITOR_MODES.NOTE
        : REPLY_EDITOR_MODES.REPLY;
    },
    hasMeaningfulEditorContent() {
      const body = this.message || '';
      // Only strip the signature when it's actually being auto-appended.
      // If the toggle is off, the agent's text might happen to match their
      // saved signature and we'd incorrectly treat it as empty.
      const shouldStripSignature =
        !this.isPrivate && this.sendWithSignature && !!this.messageSignature;
      if (!shouldStripSignature) return !!body.trim();
      const stripped = removeSignature(
        body,
        this.messageSignature,
        getEffectiveChannelType(this.channelType, this.inbox?.medium || '')
      );
      return !!stripped.trim();
    },
    isBotOwnedPendingConversation() {
      return (
        this.currentChat?.status === wootConstants.STATUS_TYPE.PENDING &&
        this.currentChat?.meta?.assignee_type === 'AgentBot'
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      if (this.isEditorDisabled) {
        if (this.isWhatsappCloudContactBlocked) {
          return this.$t('CONVERSATION.FOOTER.WHATSAPP_CONTACT_BLOCKED');
        }
        if (this.isAWhatsAppChannel) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_WHATSAPP');
        }
        if (this.isAPIInbox) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_API');
        }
        return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED');
      }
      return this.isPrivate
        ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (this.isEditorDisabled) return true;
      if (this.isATwitterInbox) return true;
      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAnInstagramChannel) {
        return MESSAGE_MAX_LENGTH.INSTAGRAM;
      }
      if (this.isATelegramChannel) {
        return MESSAGE_MAX_LENGTH.TELEGRAM;
      }
      if (this.isATiktokChannel) {
        return MESSAGE_MAX_LENGTH.TIKTOK;
      }
      if (this.isATwilioWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isAWhatsAppCloudChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAnEmailChannel) {
        return MESSAGE_MAX_LENGTH.EMAIL;
      }
      if (this.isATwilioSMSChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      const { image_send: imageSend } =
        this.currentChat?.additional_attributes?.tiktok_capabilities ?? {};
      const tiktokAttachmentSupported = imageSend ?? true;

      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isALineChannel ||
        this.isAnInstagramChannel ||
        (this.isATiktokChannel && tiktokAttachmentSupported)
      );
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? `(${this.shortcutKey})`
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel ||
        this.isATelegramChannel
      );
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.messageSignature;
    },
    sendWithSignature() {
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.effectiveReplyMode}`;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppCloudChannel) {
        return AUDIO_FORMATS.OGG;
      }
      if (this.isAWhatsAppChannel || this.isATelegramChannel) {
        return AUDIO_FORMATS.MP3;
      }
      if (this.isAPIInbox) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
        contact: this.currentContact,
        inbox: this.inbox,
      });
      // Match the backend drops: names are Ruby-capitalized and
      // {{agent.*}} is the message sender, not the assignee.
      return {
        ...variables,
        ...getContactVariables(this.currentContact),
        ...getAgentVariables(this.currentUser),
      };
    },
    connectedPortalSlug() {
      const { help_center: portal = {} } = this.inbox;
      const { slug = '' } = portal;
      return slug;
    },
    quotedReplyPreference() {
      if (!this.isAnEmailChannel) {
        return false;
      }

      return !!this.fetchQuotedReplyFlagFromUISettings(this.channelType);
    },
    lastEmailWithQuotedContent() {
      if (!this.isAnEmailChannel) {
        return null;
      }

      const lastEmail = this.lastEmail;
      if (!lastEmail || lastEmail.private) {
        return null;
      }

      return lastEmail;
    },
    quotedEmailText() {
      return extractQuotedEmailText(this.lastEmailWithQuotedContent);
    },
    quotedEmailPreviewText() {
      return truncatePreviewText(this.quotedEmailText, 80);
    },
    shouldShowQuotedReplyToggle() {
      return this.isAnEmailChannel && !this.isOnPrivateNote;
    },
    shouldShowQuotedPreview() {
      return (
        this.shouldShowQuotedReplyToggle &&
        this.quotedReplyPreference &&
        !!this.quotedEmailText
      );
    },
    isWhatsappCloudContactBlocked() {
      return (
        this.isAWhatsAppCloudChannel &&
        !this.isOnPrivateNote &&
        !!this.currentContact?.blocked
      );
    },
    isDefaultEditorMode() {
      return !this.showAudioRecorderEditor && !this.copilot.isActive.value;
    },
    isEditorDisabled() {
      return (
        this.isWhatsappCloudContactBlocked ||
        (this.isAWhatsAppChannel || this.isAPIInbox) &&
        !this.isOnPrivateNote &&
        !this.currentChat.can_reply
      );
    },
  },
  watch: {
    currentChat(conversation, oldConversation) {
      if (oldConversation && oldConversation.id !== conversation.id) {
        this.resetInternalChatConversationOption();
        // Only update email fields when switching to a completely different conversation (by ID)
        // This prevents overwriting user input (e.g., CC/BCC fields) when performing actions
        // like self-assign or other updates that do not actually change the conversation context
        this.setCCAndToEmailsFromLastChat();
        // Reset Copilot editor state (includes cancelling ongoing generation)
        this.copilot.reset();
      }

      if (this.isInstagramReplyRestricted) {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
        return;
      }

      if (this.isOnPrivateNote) {
        return;
      }

      this.replyType = this.isWithinMessagingWindow
        ? REPLY_EDITOR_MODES.REPLY
        : REPLY_EDITOR_MODES.NOTE;

      this.fetchAndSetReplyTo();
    },
    // When moving from one conversation to another, the store may not have the
    // list of all the messages. A fetch is subsequently made to get the messages.
    // This watcher handles two main cases:
    // 1. When switching conversations and messages are fetched/updated, ensures CC/BCC fields are set from the latest OUTGOING/INCOMING email (not activity/private messages).
    // 2. Fixes and issue where CC/BCC fields could be reset/lost after assignment/activity actions or message mutations that did not represent a true email context change.
    lastEmail: {
      handler(lastEmail) {
        if (!lastEmail) return;
        this.setCCAndToEmailsFromLastChat();
      },
      deep: true,
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.switchDraftContext(conversationId, this.effectiveReplyMode);
        this.resetRecorderAndClearAttachments();
      }
    },
    message() {
      // Autosave the current message draft.
      this.doAutoSaveDraft();
    },
    showWhatsappTemplates(isAvailable) {
      if (!isAvailable) this.hideWhatsappTemplatesModal();
    },
    showContentTemplates(isAvailable) {
      if (!isAvailable) this.hideContentTemplatesModal();
    },
    effectiveReplyMode(updatedReplyType) {
      if (updatedReplyType !== REPLY_EDITOR_MODES.NOTE) {
        this.resetInternalChatConversationOption();
      }
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode: updatedReplyType,
      });
      this.switchDraftContext(this.conversationIdByRoute, updatedReplyType);
    },
  },

  mounted() {
    if (this.isInstagramReplyRestricted) {
      this.replyType = REPLY_EDITOR_MODES.NOTE;
    }

    this.$store.dispatch('draftMessages/setReplyEditorMode', {
      mode: this.effectiveReplyMode,
    });
    this.switchDraftContext(
      this.conversationIdByRoute,
      this.effectiveReplyMode
    );
    // Bound directly rather than through useKeyboardEvents, because this has to
    // keep working even while the editor is focussed.
    document.addEventListener('paste', this.onPaste);
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.effectiveReplyMode);
      },
      500,
      true
    );

    this.fetchAndSetReplyTo();
    emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);

    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    emitter.on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.on(CMD_AI_ASSIST, this.executeCopilotAction);

    this.refreshMediaInputDevices();
    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener(
        'devicechange',
        this.refreshMediaInputDevices
      );
    }
  },
  unmounted() {
    document.removeEventListener('paste', this.onPaste);
    emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);
    emitter.off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.off(CMD_AI_ASSIST, this.executeCopilotAction);
    if (navigator.mediaDevices?.removeEventListener) {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        this.refreshMediaInputDevices
      );
    }
  },
  methods: {
    openContactInfoTemplateModal() {
      this.requestContactInfoTemplatesOnly = true;
      this.showWhatsAppTemplatesModal = true;
    },
    getDraftKey(
      conversationId = this.conversationIdByRoute,
      replyType = this.effectiveReplyMode
    ) {
      return `draft-${conversationId}-${replyType}`;
    },
    getCopilotAcceptedMessage(replyType = this.effectiveReplyMode) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      return this.copilotAcceptedMessages[key] || '';
    },
    setCopilotAcceptedMessage(message, replyType = this.effectiveReplyMode) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      this.copilotAcceptedMessages[key] = trimContent(
        message || '',
        this.maxLength
      );
    },
    clearCopilotAcceptedMessage(replyType = this.effectiveReplyMode) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      delete this.copilotAcceptedMessages[key];
    },
    handleInsert(article) {
      const { url, title } = article;
      // Removing empty lines from the title
      const lines = title.split('\n');
      const nonEmptyLines = lines.filter(line => line.trim() !== '');
      const filteredMarkdown = nonEmptyLines.join(' ');
      emitter.emit(
        BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
        `[${filteredMarkdown}](${url})`
      );

      useTrack(CONVERSATION_EVENTS.INSERT_ARTICLE_LINK);
    },
    toggleQuotedReply() {
      if (!this.isAnEmailChannel) {
        return;
      }

      const nextValue = !this.quotedReplyPreference;
      this.setQuotedReplyFlagForInbox(this.channelType, nextValue);
    },
    shouldIncludeQuotedEmail() {
      return (
        this.quotedReplyPreference &&
        this.shouldShowQuotedReplyToggle &&
        !!this.quotedEmailText
      );
    },
    getMessageWithQuotedEmailText(message) {
      if (!this.shouldIncludeQuotedEmail()) {
        return message;
      }

      const quotedText = this.quotedEmailText || '';
      const header = buildQuotedEmailHeader(
        this.lastEmailWithQuotedContent,
        this.currentContact,
        this.inbox
      );

      return appendQuotedTextToMessage(message, quotedText, header);
    },
    resetRecorderAndClearAttachments() {
      // Reset audio recorder UI state
      this.resetAudioRecorderInput();
      // Reset attached files
      this.attachedFiles = [];
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = this.getDraftKey(conversationId, replyType);
        const draftToSave = trimContent(this.message || '', this.maxLength);

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    switchDraftContext(conversationId, replyMode) {
      if (
        this.draftConversationId === conversationId &&
        this.draftReplyMode === replyMode
      ) {
        return;
      }

      if (this.draftConversationId) {
        this.setToDraft(this.draftConversationId, this.draftReplyMode);
      }

      this.draftConversationId = conversationId;
      this.draftReplyMode = replyMode;
      this.getFromDraft();
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        // ensure that the message has signature set based on the ui setting
        this.message = this.toggleSignatureForDraft(messageFromStore);
      }
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivate) {
        return message;
      }

      // Even when editor is disabled (e.g. WhatsApp/API can't reply), we must
      // still normalize stale signatures out of drafts when signature is off.
      if (this.isEditorDisabled && this.sendWithSignature) {
        return message;
      }

      const effectiveChannelType = getEffectiveChannelType(
        this.channelType,
        this.inbox?.medium || ''
      );

      return this.sendWithSignature
        ? appendSignature(message, this.messageSignature, effectiveChannelType)
        : removeSignature(message, this.messageSignature, effectiveChannelType);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    resetInternalChatConversationOption() {
      this.createInternalChatConversation = false;
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        !this.showMacrosMenu &&
        this.isFocused &&
        this.isEditorHotKeyEnabled(selectedKey)
      );
    },
    onPaste(e) {
      // Don't handle paste if compose new conversation modal is open
      if (this.newConversationModalActive) return;

      // Don't handle paste if editor is disabled
      if (this.isEditorDisabled) return;
      if (!this.showFileUpload && !this.isOnPrivateNote) return;

      // Filter valid files (non-zero size)
      Array.from(e.clipboardData.files)
        .filter(file => file.size > 0)
        .filter(file => {
          const isAllowed = isFileTypeAllowedForChannel(file, {
            channelType: this.channelType || this.inbox?.channel_type,
            medium: this.inbox?.medium,
            conversationType: this.conversationType,
            isInstagramChannel: this.isAnInstagramChannel,
            isOnPrivateNote: this.isOnPrivateNote,
          });

          if (!isAllowed) {
            useAlert(
              this.$t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', {
                fileName: file.name,
              })
            );
          }

          return isAllowed;
        })
        .forEach(file => {
          const { name, type, size } = file;
          this.onFileUpload({ name, type, size, file });
        });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    toggleMacrosMenu(value) {
      this.showMacrosMenu = value;
    },
    onExecuteMacro(macro) {
      const pending = this.macroExecution.execute(macro, this.currentChat.id);
      if (pending) {
        this.$refs.resolveAttributesModal?.open(
          pending.missing,
          pending.customAttributes
        );
      }
    },
    openWhatsappTemplateModal() {
      this.requestContactInfoTemplatesOnly = false;
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
      this.requestContactInfoTemplatesOnly = false;
    },
    openWhatsappFlowModal() {
      this.showWhatsAppFlowsModal = true;
    },
    hideWhatsappFlowsModal() {
      this.showWhatsAppFlowsModal = false;
    },
    openWhatsappCommerceModal() {
      this.showWhatsAppCommerceModal = true;
    },
    hideWhatsappCommerceModal() {
      this.showWhatsAppCommerceModal = false;
    },
    openContentTemplateModal() {
      this.showContentTemplatesModal = true;
    },
    hideContentTemplatesModal() {
      this.showContentTemplatesModal = false;
    },
    confirmOnSendReply() {
      if (this.isReplyButtonDisabled) {
        return;
      }
      if (!this.showMentions) {
        const copilotAcceptedMessage = this.getCopilotAcceptedMessage();
        const isOnWhatsApp =
          this.isATwilioWhatsAppChannel ||
          this.isAWhatsAppCloudChannel ||
          this.is360DialogWhatsAppChannel;
        // Instagram and TikTok do not support sending text and attachments in the same message.
        // For Instagram, combining them causes duplicate messages due to separate echo events per component.
        // For TikTok, the API rejects messages that mix text and media.
        // To handle both cases, text and attachments are always sent as separate messages.
        const isOnInstagram = this.isAnInstagramChannel;
        const isOnTiktok = this.isATiktokChannel;
        if ((isOnWhatsApp || isOnInstagram || isOnTiktok) && !this.isPrivate) {
          this.sendMessageAsMultipleMessages(
            this.message,
            copilotAcceptedMessage
          );
        } else {
          const messagePayload = this.getMessagePayload(this.message);
          this.sendMessage(
            messagePayload,
            this.message,
            copilotAcceptedMessage
          );
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }

        this.clearMessage();
        this.hideEmojiPicker();
      }
    },
    sendMessageAsMultipleMessages(message, copilotAcceptedMessage = '') {
      const messages = this.getMultipleMessagesPayload(message);
      messages.forEach(messagePayload => {
        this.sendMessage(
          messagePayload,
          messagePayload.message || '',
          copilotAcceptedMessage
        );
      });
    },
    sendMessageAnalyticsData(
      isPrivate,
      { editorMessage = '', copilotAcceptedMessage = '' } = {}
    ) {
      const normalizeForComparison = message => {
        let normalizedMessage = message || '';

        if (this.sendWithSignature && this.messageSignature && !isPrivate) {
          const effectiveChannelType = getEffectiveChannelType(
            this.channelType,
            this.inbox?.medium || ''
          );
          normalizedMessage = removeSignature(
            normalizedMessage,
            this.messageSignature,
            effectiveChannelType
          );
        }

        return trimContent(normalizedMessage);
      };

      const normalizedAcceptedMessage = normalizeForComparison(
        copilotAcceptedMessage
      );
      const normalizedEditorMessage = normalizeForComparison(editorMessage);

      if (normalizedAcceptedMessage && normalizedEditorMessage) {
        useTrack(CAPTAIN_EVENTS.AI_ASSISTED_MESSAGE_SENT, {
          conversationId: this.conversationIdByRoute,
          channelType: this.channelType,
          editedBeforeSend:
            normalizedAcceptedMessage !== normalizedEditorMessage,
          isPrivate,
        });
      }

      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? useTrack(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : useTrack(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          this.confirmOnSendReply();
        }
      } else {
        this.confirmOnSendReply();
      }
    },
    async sendMessage(
      messagePayload,
      editorMessage = '',
      copilotAcceptedMessage = ''
    ) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        await this.createInternalConversationFromNote(editorMessage);
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private, {
          editorMessage,
          copilotAcceptedMessage,
        });
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
      }
    },
    async createInternalConversationFromNote(editorMessage = '') {
      const noteMessage = trimContent(editorMessage || '');
      if (
        !this.createInternalChatConversation ||
        !this.canCreateInternalChatConversation
      ) {
        return;
      }

      if (!noteMessage) {
        useAlert(
          this.$t('CONVERSATION.REPLYBOX.INTERNAL_CHAT_EMPTY_MESSAGE_ERROR')
        );
        this.resetInternalChatConversationOption();
        return;
      }

      try {
        const response = await ConversationApi.create({
          inbox_id: this.internalChatInboxId,
          contact_id: this.internalChatContactId,
          assignee_id: this.currentChat?.assignee_id || this.currentUser?.id,
          status: 'open',
          custom_attributes: {
            internal_chat: true,
            source_conversation_id: this.currentChat?.id,
            source_conversation_display_id: this.currentChat?.display_id,
            source_inbox_id: this.currentChat?.inbox_id,
          },
        });

        const createdConversationId = response?.data?.id;
        if (!createdConversationId) {
          throw new Error('internal_chat_conversation_missing_id');
        }

        await MessageApi.create({
          conversationId: createdConversationId,
          message: noteMessage,
          private: true,
        });

        useAlert(this.$t('CONVERSATION.REPLYBOX.INTERNAL_CHAT_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.INTERNAL_CHAT_ERROR'));
      } finally {
        this.resetInternalChatConversationOption();
      }
    },
    async sendQuickMessage(messagePayload) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.sendMessageAnalyticsData(messagePayload.private);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    async onSendWhatsAppFlowReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappFlowsModal();
    },
    async onSendWhatsAppCommerceReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappCommerceModal();
    },
    async onSendContentTemplateReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideContentTemplatesModal();
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      // Clear attachments when switching between private note and reply modes
      // This is to prevent from breaking the upload rules
      if (this.attachedFiles.length > 0) this.attachedFiles = [];

      this.$store.dispatch('draftMessages/setReplyEditorMode', { mode });
      if (this.canSendPublicReply) this.replyType = mode;
      if (this.isRecordingAudio) {
        this.toggleAudioRecorder();
      }
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    addIntoEditor(content) {
      this.updateEditorSelectionWith = content;
      this.onFocus();
    },
    executeCopilotAction(action, data) {
      this.copilot.execute(action, data);
    },
    clearMessage() {
      this.message = '';
      this.clearCopilotAcceptedMessage();
      this.resetInternalChatConversationOption();
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        const effectiveChannelType = getEffectiveChannelType(
          this.channelType,
          this.inbox?.medium || ''
        );
        this.message = appendSignature(
          this.message,
          this.messageSignature,
          effectiveChannelType
        );
      }
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
      this.resetAudioRecorderInput();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.toEmails = '';
    },

    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    async refreshMediaInputDevices() {
      if (!navigator.mediaDevices?.enumerateDevices) {
        this.availableAudioInputDevices = [];
        this.availableVideoInputDevices = [];
        this.hasCheckedAudioInputDevices = true;
        this.hasCheckedVideoInputDevices = true;
        this.selectedAudioInputDeviceId = '';
        this.selectedVideoInputDeviceId = '';
        return;
      }

      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const rawAudioInputDevices = devices.filter(
          device => device.kind === 'audioinput'
        );
        const availableAudioInputDevices = rawAudioInputDevices.filter(
          device => !['default', 'communications'].includes(device.deviceId)
        );
        const audioInputDevices = (
          availableAudioInputDevices.length
            ? availableAudioInputDevices
            : rawAudioInputDevices
        ).map((device, index) => ({
          deviceId: device.deviceId,
          label:
            device.label ||
            this.$t('CONVERSATION.REPLYBOX.AUDIO_INPUT_DEVICE_FALLBACK', {
              index: index + 1,
            }),
        }));
        const videoInputDevices = devices
          .filter(device => device.kind === 'videoinput')
          .map((device, index) => ({
            deviceId: device.deviceId,
            label:
              device.label ||
              this.$t('CONVERSATION.REPLYBOX.CAMERA_INPUT_DEVICE_FALLBACK', {
                index: index + 1,
              }),
          }));

        this.availableAudioInputDevices = audioInputDevices;
        this.availableVideoInputDevices = videoInputDevices;
        this.hasCheckedAudioInputDevices = true;
        this.hasCheckedVideoInputDevices = true;

        const storedAudioDeviceId = LocalStorage.get(
          LOCAL_STORAGE_KEYS.AUDIO_INPUT_DEVICE
        );
        const storedVideoDeviceId = LocalStorage.get(
          LOCAL_STORAGE_KEYS.VIDEO_INPUT_DEVICE
        );
        const nextAudioDeviceId =
          audioInputDevices.find(
            device => device.deviceId === storedAudioDeviceId
          )
            ?.deviceId || audioInputDevices[0]?.deviceId || '';
        const nextVideoDeviceId =
          videoInputDevices.find(
            device => device.deviceId === storedVideoDeviceId
          )?.deviceId || videoInputDevices[0]?.deviceId || '';

        this.selectedAudioInputDeviceId = nextAudioDeviceId;
        this.selectedVideoInputDeviceId = nextVideoDeviceId;

        if (nextAudioDeviceId) {
          LocalStorage.set(
            LOCAL_STORAGE_KEYS.AUDIO_INPUT_DEVICE,
            nextAudioDeviceId
          );
        } else {
          LocalStorage.remove(LOCAL_STORAGE_KEYS.AUDIO_INPUT_DEVICE);
        }

        if (nextVideoDeviceId) {
          LocalStorage.set(
            LOCAL_STORAGE_KEYS.VIDEO_INPUT_DEVICE,
            nextVideoDeviceId
          );
        } else {
          LocalStorage.remove(LOCAL_STORAGE_KEYS.VIDEO_INPUT_DEVICE);
        }
      } catch (error) {
        this.availableAudioInputDevices = [];
        this.availableVideoInputDevices = [];
        this.hasCheckedAudioInputDevices = true;
        this.hasCheckedVideoInputDevices = true;
        this.selectedAudioInputDeviceId = '';
        this.selectedVideoInputDeviceId = '';
        LocalStorage.remove(LOCAL_STORAGE_KEYS.AUDIO_INPUT_DEVICE);
        LocalStorage.remove(LOCAL_STORAGE_KEYS.VIDEO_INPUT_DEVICE);
      }
    },
    toggleAudioRecorder() {
      if (
        !this.isRecordingAudio &&
        this.hasCheckedAudioInputDevices &&
        !this.availableAudioInputDevices.length
      ) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_UNAVAILABLE'));
        return;
      }

      this.isRecordingAudio = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.resetAudioRecorderInput();
      }
    },
    onAudioInputDeviceChange(deviceId) {
      if (!deviceId || deviceId === this.selectedAudioInputDeviceId) {
        return;
      }

      this.selectedAudioInputDeviceId = deviceId;
      LocalStorage.set(LOCAL_STORAGE_KEYS.AUDIO_INPUT_DEVICE, deviceId);

      if (this.isRecordingAudio) {
        this.toggleAudioRecorder();
      }
    },
    onVideoInputDeviceChange(deviceId) {
      if (!deviceId || deviceId === this.selectedVideoInputDeviceId) {
        return;
      }

      this.selectedVideoInputDeviceId = deviceId;
      LocalStorage.set(LOCAL_STORAGE_KEYS.VIDEO_INPUT_DEVICE, deviceId);
    },
    openCameraCaptureModal() {
      if (
        this.hasCheckedVideoInputDevices &&
        !this.availableVideoInputDevices.length
      ) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.CAMERA_UNAVAILABLE'));
        return;
      }

      this.$refs.cameraCaptureModal?.open();
    },
    onPhotoCaptured(file) {
      return file && this.attachCapturedMedia(file);
    },
    openVideoCaptureModal(preferredMimeType = '') {
      if (
        this.hasCheckedVideoInputDevices &&
        !this.availableVideoInputDevices.length
      ) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.CAMERA_UNAVAILABLE'));
        return;
      }

      if (!preferredMimeType) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.VIDEO_CAPTURE_UNSUPPORTED'));
        return;
      }

      this.preferredVideoCaptureMimeType = preferredMimeType;
      this.$refs.videoCaptureModal?.open();
    },
    onVideoCaptured(file) {
      return file && this.attachCapturedMedia(file);
    },
    attachCapturedMedia(file) {
      const rawFile = file?.file || file;
      const isAllowed = isFileTypeAllowedForChannel(rawFile, {
        channelType: this.channelType || this.inbox?.channel_type,
        medium: this.inbox?.medium,
        conversationType: this.conversationType,
        isInstagramChannel: this.isAnInstagramChannel,
        isOnPrivateNote: this.isOnPrivateNote,
      });

      if (!isAllowed) {
        useAlert(
          this.$t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', {
            fileName: rawFile.name,
          })
        );
        return;
      }

      return this.onFileUpload(file);
    },
    toggleAudioRecorderPlayPause() {
      if (!this.$refs.audioRecorderInput) return;
      if (!this.recordingAudioState) {
        this.$refs.audioRecorderInput.stopRecording();
      } else {
        this.$refs.audioRecorderInput.playPause();
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.effectiveReplyMode);
    },
    onFocus() {
      this.isFocused = true;
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      this.hasRecordedAudio = true;
      // Added a new key isVoiceMessage to the file to identify recorded audio
      // Because to filter and show only non recorded audio and other attachments
      const autoRecordedFile = {
        ...file,
        isVoiceMessage: true,
      };
      return file && this.onFileUpload(autoRecordedFile);
    },
    onAudioRecorderError({ key }) {
      const translationKey =
        {
          conversion_failed:
            'CONVERSATION.REPLYBOX.AUDIO_CONVERSION_FAILED',
          permission: 'CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_PERMISSION',
          device_not_found:
            'CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_DEVICE_NOT_FOUND',
          device_unavailable:
            'CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_DEVICE_UNAVAILABLE',
          generic: 'CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ERROR',
        }[key] || 'CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ERROR';

      this.resetAudioRecorderInput();
      useAlert(this.$t(translationKey));
      this.refreshMediaInputDevices();
    },
    onCameraCaptureError({ key }) {
      const translationKey =
        {
          permission: 'CONVERSATION.REPLYBOX.CAMERA_PERMISSION_ERROR',
          device_not_found: 'CONVERSATION.REPLYBOX.CAMERA_DEVICE_NOT_FOUND',
          device_unavailable:
            'CONVERSATION.REPLYBOX.CAMERA_DEVICE_UNAVAILABLE',
          generic: 'CONVERSATION.REPLYBOX.CAMERA_CAPTURE_ERROR',
        }[key] || 'CONVERSATION.REPLYBOX.CAMERA_CAPTURE_ERROR';

      useAlert(this.$t(translationKey));
      this.refreshMediaInputDevices();
    },
    onVideoCaptureError({ key }) {
      const translationKey =
        {
          permission: 'CONVERSATION.REPLYBOX.VIDEO_PERMISSION_ERROR',
          device_not_found: 'CONVERSATION.REPLYBOX.VIDEO_DEVICE_NOT_FOUND',
          device_unavailable:
            'CONVERSATION.REPLYBOX.VIDEO_DEVICE_UNAVAILABLE',
          generic: 'CONVERSATION.REPLYBOX.VIDEO_CAPTURE_ERROR',
        }[key] || 'CONVERSATION.REPLYBOX.VIDEO_CAPTURE_ERROR';

      useAlert(this.$t(translationKey));
      this.refreshMediaInputDevices();
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    attachFile({ blob, file }) {
      if (!this.showFileUpload && !this.isOnPrivateNote) return;

      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
          isVoiceMessage: file?.isVoiceMessage || false,
        });
      };
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
    },
    setReplyToInPayload(payload) {
      if (this.inReplyTo?.id) {
        return {
          ...payload,
          contentAttributes: {
            ...payload.contentAttributes,
            in_reply_to: this.inReplyTo.id,
          },
          content_attributes: {
            ...payload.content_attributes,
            in_reply_to: this.inReplyTo.id,
          },
        };
      }

      return payload;
    },
    buildLocationAttachment(location) {
      return [
        {
          id: `location-${Date.now()}`,
          file_type: 'location',
          coordinates_lat: location.latitude,
          coordinates_long: location.longitude,
          fallback_title: location.fallback_title,
          external_url:
            location.external_url ||
            `https://maps.google.com/?q=${location.latitude},${location.longitude}`,
        },
      ];
    },
    openLocationMessageModal() {
      this.$refs.locationMessageModal?.open();
    },
    buildLocationMessagePayload(location) {
      let messagePayload = {
        conversationId: this.currentChat.id,
        message: '',
        private: false,
        sender: this.sender,
        attachments: this.buildLocationAttachment(location),
        contentAttributes: {
          location,
        },
        content_attributes: {
          location,
        },
      };

      messagePayload = this.setReplyToInPayload(messagePayload);
      return messagePayload;
    },
    async onSendLocationMessage(location) {
      await this.sendQuickMessage(this.buildLocationMessagePayload(location));
    },
    openStickerCatalogModal() {
      this.$refs.stickerCatalogModal?.open();
    },
    buildStickerMessagePayload(sticker) {
      let messagePayload = {
        conversationId: this.currentChat.id,
        message: '',
        private: false,
        sender: this.sender,
        files: [sticker.blob_signed_id],
        attachments: [
          {
            id: `sticker-${Date.now()}`,
            file_type: 'image',
            data_url: sticker.file_url,
            thumb_url: sticker.file_url,
            extension: 'webp',
          },
        ],
        contentType: 'sticker',
        content_type: 'sticker',
        contentAttributes: {
          sticker_catalog_id: sticker.id,
        },
        content_attributes: {
          sticker_catalog_id: sticker.id,
        },
      };

      messagePayload = this.setReplyToInPayload(messagePayload);
      return messagePayload;
    },
    async onSelectSticker(sticker) {
      await this.sendQuickMessage(this.buildStickerMessagePayload(sticker));
    },
    getMultipleMessagesPayload(message) {
      const multipleMessagePayload = [];

      if (this.attachedFiles && this.attachedFiles.length) {
        let caption =
          this.isAnInstagramChannel || this.isATiktokChannel ? '' : message;
        this.attachedFiles.forEach(attachment => {
          const attachedFile = this.globalConfig.directUploadsEnabled
            ? attachment.blobSignedId
            : attachment.resource.file;
          let attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile],
            private: false,
            message: caption,
            sender: this.sender,
            isVoiceMessage: attachment.isVoiceMessage || false,
          };

          attachmentPayload = this.setReplyToInPayload(attachmentPayload);
          multipleMessagePayload.push(attachmentPayload);
          // For WhatsApp, only the first attachment gets a caption
          if (!this.isAnInstagramChannel) caption = '';
        });
      }

      const hasNoAttachments =
        !this.attachedFiles || !this.attachedFiles.length;
      // For Instagram and TikTok, text must always be sent as a separate message (no captions on attachments).
      // For WhatsApp, we only need a text message if there are no attachments.
      if (
        ((this.isAnInstagramChannel || this.isATiktokChannel) &&
          this.message) ||
        (!(this.isAnInstagramChannel || this.isATiktokChannel) &&
          hasNoAttachments)
      ) {
        let messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
        };

        messagePayload = this.setReplyToInPayload(messagePayload);

        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      const messageWithQuote = this.getMessageWithQuotedEmailText(message);

      let messagePayload = {
        conversationId: this.currentChat.id,
        message: messageWithQuote,
        private: this.isPrivate,
        sender: this.sender,
      };
      messagePayload = this.setReplyToInPayload(messagePayload);

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          if (this.globalConfig.directUploadsEnabled) {
            messagePayload.files.push(attachment.blobSignedId);
            if (attachment.isVoiceMessage) {
              messagePayload.isVoiceMessage = true;
            }
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }
      return messagePayload;
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCAndToEmailsFromLastChat() {
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      const { email: inboxEmail, forward_to_email: forwardToEmail } =
        this.inbox;

      const { cc, bcc, to } = getRecipients(
        this.lastEmail,
        conversationContact,
        inboxEmail,
        forwardToEmail
      );

      this.toEmails = to.join(', ');
      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
    },
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      this.inReplyTo = this.currentChat?.messages?.find(message => {
        if (message.id === replyToMessageId) {
          return true;
        }
        return false;
      });
    },
    onReplyToMessage() {
      this.fetchAndSetReplyTo();
      if (this.inReplyTo) {
        this.$nextTick(() => {
          const pos = this.isSignatureEnabledForInbox ? 'start' : 'end';
          this.messageEditor?.focusEditorInputField(pos);
        });
      }
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
    onSearchPopoverClose() {
      this.showArticleSearchPopover = false;
    },
    toggleInsertArticle() {
      this.showArticleSearchPopover = !this.showArticleSearchPopover;
    },
    resetAudioRecorderInput() {
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.hasRecordedAudio = false;
      // Only clear the recorded audio when we click toggle button.
      this.attachedFiles = this.attachedFiles.filter(
        file => !file?.isVoiceMessage
      );
    },
    toggleEditorSize() {
      this.$emit('toggleEditorSize');
      this.$nextTick(() => this.messageEditor?.focusEditorInputField());
    },
    onSubmitCopilotReply() {
      const acceptedMessage = this.copilot.accept();
      this.message = acceptedMessage;
      this.setCopilotAcceptedMessage(acceptedMessage);
    },
  },
};
</script>

<template>
  <ReplyBoxBanner :message="message" :is-on-private-note="isOnPrivateNote" />
  <div class="reply-box" :class="replyBoxClass">
    <div
      v-if="isWhatsappCloudContactBlocked"
      class="mb-3 rounded-xl border border-n-ruby-6 bg-n-ruby-3/20 px-4 py-3 text-sm text-n-ruby-11"
    >
      {{ $t('CONVERSATION.FOOTER.WHATSAPP_CONTACT_BLOCKED_BANNER') }}
    </div>
    <ReplyTopPanel
      :mode="replyType"
      :conversation-id="conversationId"
      :is-reply-restricted="!canSendPublicReply"
      :disabled="
        (copilot.isActive.value && copilot.isButtonDisabled.value) ||
        showAudioRecorderEditor
      "
      :is-editor-disabled="isEditorDisabled"
      :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
      :characters-remaining="charactersRemaining"
      :editor-content="message"
      :has-content="hasMeaningfulEditorContent"
      @set-reply-mode="setReplyMode"
      @toggle-editor-size="toggleEditorSize"
      @toggle-copilot="copilot.toggleEditor"
      @execute-copilot-action="executeCopilotAction"
    />
    <ArticleSearchPopover
      v-if="showArticleSearchPopover && connectedPortalSlug"
      :selected-portal-slug="connectedPortalSlug"
      @insert="handleInsert"
      @close="onSearchPopoverClose"
    />
    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <div :key="copilot.editorTransitionKey.value" class="reply-box__top">
        <CameraCaptureModal
          ref="cameraCaptureModal"
          :selected-video-input-device-id="selectedVideoInputDeviceId"
          @capture-photo="onPhotoCaptured"
          @camera-error="onCameraCaptureError"
        />
        <LocationMessageModal
          ref="locationMessageModal"
          @send-location="onSendLocationMessage"
        />
        <StickerCatalogModal
          ref="stickerCatalogModal"
          :can-manage="currentRole === 'administrator'"
          @select-sticker="onSelectSticker"
        />
        <VideoCaptureModal
          ref="videoCaptureModal"
          :selected-video-input-device-id="selectedVideoInputDeviceId"
          :selected-audio-input-device-id="selectedAudioInputDeviceId"
          :preferred-video-mime-type="preferredVideoCaptureMimeType"
          @capture-video="onVideoCaptured"
          @video-error="onVideoCaptureError"
        />
        <ReplyToMessage
          v-if="shouldShowReplyToMessage"
          :message="inReplyTo"
          @dismiss="resetReplyToMessage"
        />
        <EmojiIconPicker
          v-if="showEmojiPicker"
          v-on-clickaway="hideEmojiPicker"
          mode="emoji"
          class="emoji-dialog"
          :class="{
            'emoji-dialog--expanded': isOnExpandedLayout,
          }"
          @select="addIntoEditor($event.value)"
        />
        <ReplyEmailHead
          v-if="showReplyHead && isDefaultEditorMode"
          v-model:cc-emails="ccEmails"
          v-model:bcc-emails="bccEmails"
          v-model:to-emails="toEmails"
        />
        <AudioRecorder
          v-if="showAudioRecorderEditor"
          ref="audioRecorderInput"
          :audio-record-format="audioRecordFormat"
          :selected-audio-input-device-id="selectedAudioInputDeviceId"
          @recorder-progress-changed="onRecordProgressChanged"
          @finish-record="onFinishRecorder"
          @recording-error="onAudioRecorderError"
          @play="recordingAudioState = 'playing'"
          @pause="recordingAudioState = 'paused'"
        />
        <CopilotEditorSection
          v-if="copilot.isActive.value && !showAudioRecorderEditor"
          :show-copilot-editor="copilot.showEditor.value"
          :is-generating-content="copilot.isGenerating.value"
          :generated-content="copilot.generatedContent.value"
          :placeholder="$t('CONVERSATION.FOOTER.COPILOT_MSG_INPUT')"
          @focus="onFocus"
          @blur="onBlur"
          @clear-selection="clearEditorSelection"
          @close="copilot.showEditor.value = false"
          @content-ready="copilot.setContentReady"
          @send="copilot.sendFollowUp"
        />
        <WootMessageEditor
          v-else-if="!showAudioRecorderEditor"
          ref="messageEditor"
          v-model="message"
          :conversation-id="conversationId"
          :editor-id="editorStateId"
          class="input popover-prosemirror-menu"
          :is-private="isOnPrivateNote"
          :placeholder="messagePlaceHolder"
          :update-selection-with="updateEditorSelectionWith"
          :min-height="4"
          :disabled="isEditorDisabled"
          enable-insert-events
          :enable-macros="isMacrosEnabled"
          enable-variables
          :variables="messageVariables"
          :signature="messageSignature"
          allow-signature
          :channel-type="channelType"
          :medium="inbox.medium"
          @typing-off="onTypingOff"
          @typing-on="onTypingOn"
          @focus="onFocus"
          @blur="onBlur"
          @toggle-user-mention="toggleUserMention"
          @toggle-canned-menu="toggleCannedMenu"
          @toggle-variables-menu="toggleVariablesMenu"
          @toggle-macros-menu="toggleMacrosMenu"
          @execute-macro="onExecuteMacro"
          @clear-selection="clearEditorSelection"
          @execute-copilot-action="executeCopilotAction"
        />

        <QuotedEmailPreview
          v-if="shouldShowQuotedPreview && isDefaultEditorMode"
          :quoted-email-text="quotedEmailText"
          :preview-text="quotedEmailPreviewText"
          class="mb-2"
          @toggle="toggleQuotedReply"
        />

        <div
          v-if="canCreateInternalChatConversation && isDefaultEditorMode"
          class="mb-3 rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3"
        >
          <label class="flex items-start gap-3 cursor-pointer">
            <input
              v-model="createInternalChatConversation"
              type="checkbox"
              class="mt-1 rounded border-n-weak text-n-brand"
            />
            <span class="space-y-1">
              <span class="block text-sm font-medium text-n-slate-12">
                {{ $t('CONVERSATION.REPLYBOX.CREATE_INTERNAL_CHAT') }}
              </span>
              <span class="block text-sm text-n-slate-11">
                {{
                  $t('CONVERSATION.REPLYBOX.CREATE_INTERNAL_CHAT_HELP', {
                    inboxName: internalChatInbox?.name,
                  })
                }}
              </span>
            </span>
          </label>
        </div>

        <div
          v-if="hasAttachments && isDefaultEditorMode"
          class="bg-transparent py-0 mb-2"
          @paste="onPaste"
        >
          <AttachmentPreview
            class="mt-2"
            :attachments="attachedFiles"
            @remove-attachment="removeAttachment"
          />
        </div>
        <MessageSignatureMissingAlert
          v-if="
            isSignatureEnabledForInbox &&
            !isSignatureAvailable &&
            isDefaultEditorMode
          "
          class="mb-2"
        />
      </div>
    </Transition>

    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <CopilotReplyBottomPanel
        v-if="copilot.isActive.value"
        key="copilot-bottom-panel"
        :is-generating-content="copilot.isButtonDisabled.value"
        @submit="onSubmitCopilotReply"
        @cancel="copilot.reset"
      />
      <ReplyBottomPanel
        v-else
        key="reply-bottom-panel"
        :conversation-id="conversationId"
        :enable-multiple-file-upload="enableMultipleFileUpload"
        :enable-whats-app-templates="showWhatsappTemplates"
        :enable-whats-app-flows="showWhatsappFlows"
        :enable-whats-app-commerce="showWhatsappCommerce"
        :enable-content-templates="showContentTemplates"
        :inbox="inbox"
        :is-on-private-note="isOnPrivateNote"
        :is-recording-audio="isRecordingAudio"
        :is-send-disabled="isReplyButtonDisabled"
        :is-note="isPrivate"
        :is-editor-disabled="isEditorDisabled"
        :audio-input-devices="availableAudioInputDevices"
        :has-checked-audio-input-devices="hasCheckedAudioInputDevices"
        :video-input-devices="availableVideoInputDevices"
        :has-checked-video-input-devices="hasCheckedVideoInputDevices"
        :selected-audio-input-device-id="selectedAudioInputDeviceId"
        :selected-video-input-device-id="selectedVideoInputDeviceId"
        :on-file-upload="onFileUpload"
        :on-send="onSendReply"
        :on-audio-input-device-change="onAudioInputDeviceChange"
        :on-video-input-device-change="onVideoInputDeviceChange"
        :on-open-camera-capture="openCameraCaptureModal"
        :on-open-location-message="openLocationMessageModal"
        :on-open-sticker-catalog="openStickerCatalogModal"
        :on-open-video-capture="openVideoCaptureModal"
        :conversation-type="conversationType"
        :recording-audio-duration-text="recordingAudioDurationText"
        :recording-audio-state="recordingAudioState"
        :send-button-text="replyButtonLabel"
        :show-audio-recorder="showAudioRecorder"
        :show-emoji-picker="showEmojiPicker"
        :show-file-upload="showFileUpload"
        :show-quoted-reply-toggle="shouldShowQuotedReplyToggle"
        :quoted-reply-enabled="quotedReplyPreference"
        :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
        :toggle-audio-recorder="toggleAudioRecorder"
        :toggle-emoji-picker="toggleEmojiPicker"
        :message="message"
        :portal-slug="connectedPortalSlug"
        :new-conversation-modal-active="newConversationModalActive"
        @select-whatsapp-template="openWhatsappTemplateModal"
        @select-whatsapp-flow="openWhatsappFlowModal"
        @select-whatsapp-commerce="openWhatsappCommerceModal"
        @select-content-template="openContentTemplateModal"
        @toggle-insert-article="toggleInsertArticle"
        @toggle-quoted-reply="toggleQuotedReply"
        @request-contact-info-template="openContactInfoTemplateModal"
      />
    </Transition>

    <WhatsappTemplates
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      :send-rendered-content="isAPIInbox"
      :request-contact-info-only="requestContactInfoTemplatesOnly"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />

    <WhatsappFlows
      :inbox-id="inbox.id"
      :show="showWhatsAppFlowsModal"
      @close="hideWhatsappFlowsModal"
      @on-send="onSendWhatsAppFlowReply"
      @cancel="hideWhatsappFlowsModal"
    />

    <WhatsappCommerce
      :inbox-id="inbox.id"
      :show="showWhatsAppCommerceModal"
      @close="hideWhatsappCommerceModal"
      @on-send="onSendWhatsAppCommerceReply"
      @cancel="hideWhatsappCommerceModal"
    />

    <ContentTemplates
      :inbox-id="inbox.id"
      :show="showContentTemplatesModal"
      @close="hideContentTemplatesModal"
      @on-send="onSendContentTemplateReply"
      @cancel="hideContentTemplatesModal"
    />

    <ConversationResolveAttributesModal
      ref="resolveAttributesModal"
      @submit="macroExecution.submitPendingAttributes"
      @close="macroExecution.dismissPendingAttributes"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

.reply-box {
  @apply relative mb-2 mx-2 border border-n-weak rounded-xl bg-n-solid-1;

  &.is-private {
    @apply bg-n-amber-2 border-n-amber-12/10 dark:bg-n-amber-3/10 dark:border-n-amber-10/20;
  }
}

.send-button {
  @apply mb-0;
}

.reply-box__top {
  @apply relative py-0 px-3 -mt-px;
}

.emoji-dialog {
  @apply top-[unset] -bottom-10 ltr:-left-80 ltr:right-[unset] rtl:left-[unset] rtl:-right-80;

  &::before {
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
    @apply ltr:-right-4 bottom-2 rtl:-left-4 ltr:rotate-[270deg] rtl:rotate-[90deg];
  }
}

.emoji-dialog--expanded {
  @apply left-[unset] bottom-0 absolute z-[100];

  &::before {
    transform: rotate(0deg);
    @apply ltr:left-1 rtl:right-1 -bottom-2;
  }
}
</style>
